import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  PropsWithChildren,
} from "react";
import { useAtlasAuthContext } from "@/contexts/atlas-auth";
import { SessionElectricManager } from "./session-electric-manager";
import {
  SessionEventsContextValue,
  SessionEventsAction,
  ElectricSession,
  ElectricSessionEvent,
} from "./types";
import {
  sessionEventsReducer,
  createInitialState,
  selectSortedEvents,
  selectIsReady,
} from "./session-events-reducer";

const SessionEventsContext = createContext<SessionEventsContextValue | null>(null);

export const SessionEventsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(sessionEventsReducer, createInitialState());
  const { authContext } = useAtlasAuthContext();
  const managerRef = useRef<SessionElectricManager | null>(null);

  // Initialize the Electric SQL manager
  useEffect(() => {
    const manager = new SessionElectricManager({
      onSessionDataUpdate: (sessionData: ElectricSession) => {
        console.log("SessionEventsContext: Session data updated", sessionData);
        dispatch({
          type: SessionEventsAction.SESSION_DATA_UPDATED,
          payload: { sessionData },
        });
      },
      onSessionEventsUpdate: (events: ElectricSessionEvent[]) => {
        console.log("SessionEventsContext: Session events updated", { count: events.length });
        dispatch({
          type: SessionEventsAction.SESSION_EVENTS_UPDATED,
          payload: { events },
        });
      },
      onError: (error: Error) => {
        console.error("SessionEventsContext: Electric SQL error", error);
        dispatch({
          type: SessionEventsAction.SUBSCRIBE_ERROR,
          payload: { error: error.message },
        });
      },
      getAuthToken: () => authContext.authToken,
    });

    managerRef.current = manager;

    return () => {
      manager.cleanup();
    };
  }, [authContext.authToken]);

  // Subscribe to session
  const subscribeToSession = useCallback(
    async (sessionId: string): Promise<void> => {
      if (!managerRef.current) {
        return;
        // throw new Error("Electric SQL manager not initialized");
      }

      if (!authContext.authToken) {
        throw new Error("No authentication token available");
      }

      console.log(`SessionEventsContext: Subscribing to session: ${sessionId}`);

      dispatch({
        type: SessionEventsAction.SUBSCRIBE_REQUEST,
        payload: { sessionId },
      });

      try {
        await managerRef.current.subscribeToSession(sessionId);

        dispatch({
          type: SessionEventsAction.SUBSCRIBE_SUCCESS,
        });

        console.log(`SessionEventsContext: Successfully subscribed to session: ${sessionId}`);
      } catch (error) {
        console.error(`SessionEventsContext: Failed to subscribe to session: ${sessionId}`, error);
        dispatch({
          type: SessionEventsAction.SUBSCRIBE_ERROR,
          payload: { error: (error as Error).message },
        });
        throw error;
      }
    },
    [authContext.authToken]
  );

  // Unsubscribe from session
  const unsubscribeFromSession = useCallback((): void => {
    if (!managerRef.current) {
      return;
    }

    console.log("SessionEventsContext: Unsubscribing from session");
    managerRef.current.unsubscribeFromSession();

    dispatch({
      type: SessionEventsAction.UNSUBSCRIBE,
    });
  }, []);

  // Computed values
  const sortedEvents = useMemo(
    () => selectSortedEvents(state.sessionEvents),
    [state.sessionEvents]
  );
  const isReady = useMemo(() => selectIsReady(state), [state]);

  // Auto-unsubscribe when session ID changes
  useEffect(() => {
    return () => {
      if (managerRef.current) {
        managerRef.current.cleanup();
      }
    };
  }, []);

  const contextValue: SessionEventsContextValue = {
    ...state,
    subscribeToSession,
    unsubscribeFromSession,
    sortedEvents,
    isReady,
  };

  return (
    <SessionEventsContext.Provider value={contextValue}>{children}</SessionEventsContext.Provider>
  );
};

export const useSessionEvents = (): SessionEventsContextValue => {
  const context = useContext(SessionEventsContext);

  if (!context) {
    throw new Error("useSessionEvents must be used within a SessionEventsProvider");
  }

  return context;
};
